import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { api, endpoints } from "../../utils/axiosInstance";
import { App } from "antd";
import { Dropdown, Menu } from "antd";
import { DownOutlined } from "@ant-design/icons";

// ======================= Styled Components (Giữ nguyên) =======================
const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafb 100%);
  border-radius: 24px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.8);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem 2.5rem 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='0.05'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v20h40V20c0-11.046-8.954-20-20-20z'/%3E%3C/g%3E%3C/svg%3E");
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  position: relative;
  z-index: 5;
`;

const ModalSubtitle = styled.p`
  margin: 0.5rem 0 0;
  opacity: 0.9;
  font-size: 1rem;
  position: relative;
  z-index: 5;
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 2rem 2.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SearchSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 1.2rem;
`;

const LoadAllButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 16px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StatusCard = styled.div`
  background: ${(props) => {
    if (props.type === "error")
      return "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)";
    if (props.type === "loading")
      return "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)";
    return "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)";
  }};
  border: 1px solid
    ${(props) => {
      if (props.type === "error") return "#f87171";
      if (props.type === "loading") return "#60a5fa";
      return "#0ea5e9";
    }};
  border-radius: 16px;
  padding: 1.5rem;
  color: ${(props) => {
    if (props.type === "error") return "#b91c1c";
    if (props.type === "loading") return "#1d4ed8";
    return "#0c4a6e";
  }};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const RecipeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* 1 card trên 1 dòng */
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 1rem;
  width: 100%;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f8fafc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const RecipeCard = styled.div`
  background: white;
  border: 2px solid ${(props) => (props.selected ? "#667eea" : "#f1f5f9")};
  border-radius: 16px;
  padding: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  min-width: 0;

  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RecipeCardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 70px;
`;

const RecipeImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  object-fit: cover; /* Đảm bảo ảnh phủ kín mà không bị biến dạng */
  flex-shrink: 0;
  background: linear-gradient(
    135deg,
    #f1f5f9 0%,
    #e2e8f0 100%
  ); /* Hậu cảnh nếu ảnh không load */
  /* Xử lý khi ảnh không load */
  &:not([src]) {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &:not([src])::after {
    content: "🍽️";
    font-size: 1.5rem;
    opacity: 1;
  }
`;

const RecipeDetails = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RecipeName = styled.h4`
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  overflow: visible;
  white-space: normal;
  line-height: 1.4;
  word-break: break-word;

  ${RecipeCard}:hover & {
    color: #4b5cc4;
  }
`;

const RecipeCalories = styled.span`
  display: inline-block; /* Sử dụng inline-block để giới hạn kích thước theo nội dung */
  align-items: center; /* Giữ căn giữa nếu có nhiều phần tử */
  font-size: 0.85rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.15rem 0.4rem; /* Giảm padding để vừa khít hơn */
  border-radius: 10px;
  font-weight: 500;
  gap: 0.2rem; /* Giảm khoảng cách giữa icon và text */
  white-space: nowrap; /* Ngăn xuống dòng */
  width: fit-content; /* Giới hạn chiều rộng theo nội dung */

  ${RecipeCard}:hover & {
    background: #e0e7ff;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8fafc;
  border-radius: 10px;
  padding: 0.25rem 0.5rem;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: ${(props) => (props.disabled ? "#e2e8f0" : "#667eea")};
  color: ${(props) => (props.disabled ? "#94a3b8" : "white")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: #5a67d8;
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const QuantityDisplay = styled.span`
  min-width: 30px;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  color: #334155;

  ${RecipeCard}:hover & {
    color: #4b5cc4;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #64748b;
  background: #f9fafb;
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.4;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
`;

const SearchTips = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
  border-left: 4px solid #667eea;
`;

// ... (Giữ nguyên các styled components khác)

const FloatingActionButton = styled.button`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 1.5rem 2.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 16px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.5);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

// ======================= Helper Functions =======================
const removeVietnameseTones = (str) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Loại bỏ dấu
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
};

// ======================= Component Logic =======================
const AddRecipeModal = ({ isOpen, onClose, onAddRecipe, mealType }) => {
  const { message: antdMessage } = App.useApp();

  const convertMealTypeToVietnamese = (mealType) => {
    const mealTypeMap = {
      breakfast: "bữa sáng",
      lunch: "bữa trưa",
      dinner: "bữa tối",
      snack: "tráng miệng",
    };
    return mealTypeMap[mealType] || mealType; // Trả về giá trị gốc nếu không tìm thấy
  };

  const mealTypeVietnamese = convertMealTypeToVietnamese(mealType);

  const [searchTerm, setSearchTerm] = useState("");
  const [allRecipes, setAllRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [filterMode, setFilterMode] = useState("all");

  // Hàm load tất cả recipes ban đầu
  const loadAllRecipesInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDebugInfo("Đang tải danh sách món ăn...");

    setAllRecipes([]);
    setFilteredRecipes([]);
    setSelectedRecipes({});

    try {
      const response = await api.get(
        endpoints.recipes.getByCategory(mealTypeVietnamese)
      );

      let recipesData = [];
      if (response.data && Array.isArray(response.data)) {
        recipesData = response.data;
      } else {
        throw new Error("Định dạng dữ liệu không hợp lệ từ API.");
      }

      setAllRecipes(recipesData);
      setFilteredRecipes(recipesData);
      setDebugInfo(`📊 Đã tải ${recipesData.length} món ăn từ cơ sở dữ liệu.`);
    } catch (err) {
      setError(
        `Không thể tải danh sách món ăn: ${err.message}. Vui lòng kiểm tra kết nối API.`
      );
      setDebugInfo(`❌ Lỗi API: ${err.message}`);
      setAllRecipes([]);
      setFilteredRecipes([]);
    } finally {
      setLoading(false);
    }
  }, [mealTypeVietnamese]);

  const fetchSuggestedRecipes = async () => {
    setLoading(true);
    setError(null);
    setDebugInfo("🔮 Đang gọi AI gợi ý món ăn...");

    setAllRecipes([]);
    setFilteredRecipes([]);
    setSelectedRecipes({});

    try {
      const response = await api.post(endpoints.ai.suggestRecipe, {
        mealType: mealTypeVietnamese,
      });

      const recipes = response.recipes || [];

      setAllRecipes(recipes);
      setFilteredRecipes(recipes);
      setDebugInfo(`✨ Gợi ý được ${recipes.length} món ăn phù hợp từ AI.`);
    } catch (err) {
      setError(
        `Không thể lấy gợi ý từ AI: ${err.message}. Vui lòng thử lại sau.`
      );
      setDebugInfo(`❌ Lỗi AI API: ${err.message}`);
      setAllRecipes([]);
      setFilteredRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset state khi modal đóng/mở
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setAllRecipes([]);
      setFilteredRecipes([]);
      setSelectedRecipes({});
      setError(null);
      setDebugInfo("");
    } else {
      loadAllRecipesInitial();
    }
  }, [isOpen, loadAllRecipesInitial]);

  // Cải tiến logic tìm kiếm
  useEffect(() => {
    if (allRecipes.length > 0 || searchTerm !== "") {
      const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();

      // Nếu không có từ khóa tìm kiếm, hiển thị tất cả
      if (lowerCaseSearchTerm === "") {
        setFilteredRecipes(allRecipes);
        setDebugInfo(`📊 Hiển thị tất cả ${allRecipes.length} món ăn.`);
        return;
      }

      // Chuyển từ khóa tìm kiếm về không dấu
      const searchTermWithoutTones = removeVietnameseTones(lowerCaseSearchTerm);

      let currentFiltered = allRecipes.filter((recipe) => {
        if (!recipe.name || typeof recipe.name !== "string") {
          return false;
        }

        const recipeName = recipe.name.toLowerCase();
        const recipeNameWithoutTones = removeVietnameseTones(recipeName);

        // Tìm kiếm đa dạng:
        return (
          // 1. Tìm kiếm chính xác có dấu
          recipeName.includes(lowerCaseSearchTerm) ||
          // 2. Tìm kiếm không dấu
          recipeNameWithoutTones.includes(searchTermWithoutTones) ||
          // 3. Tìm kiếm từng ký tự riêng lẻ (cho phép gõ tắt như "c" -> "cơm")
          searchTermWithoutTones
            .split("")
            .every((char) => recipeNameWithoutTones.includes(char)) ||
          // 4. Tìm kiếm theo từ khóa tách riêng (space separated)
          lowerCaseSearchTerm.split(" ").every((word) => {
            const wordWithoutTones = removeVietnameseTones(word);
            return (
              recipeName.includes(word) ||
              recipeNameWithoutTones.includes(wordWithoutTones)
            );
          })
        );
      });

      setFilteredRecipes(currentFiltered);
      setDebugInfo(
        `📊 Tìm thấy ${currentFiltered.length} món ăn phù hợp với "${searchTerm}".`
      );
    } else {
      setFilteredRecipes([]);
      setDebugInfo(
        'Nhập từ khóa để tìm kiếm hoặc nhấn "Tất cả món ăn" để xem danh sách đầy đủ.'
      );
    }
  }, [searchTerm, allRecipes]);

  const handleQuantityChange = useCallback((recipeId, change) => {
    setSelectedRecipes((prev) => {
      const currentQuantity = prev[recipeId] || 0;
      const newQuantity = Math.max(0, currentQuantity + change);

      if (newQuantity === 0) {
        const { [recipeId]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [recipeId]: newQuantity,
      };
    });
  }, []);

  // Tính tổng calo của các món đã chọn trong modal
  const calculateSelectedRecipesCalories = useCallback(() => {
    let totalCalories = 0;
    Object.keys(selectedRecipes).forEach((recipeId) => {
      const recipe = allRecipes.find((r) => r._id === recipeId);
      if (recipe && typeof recipe.calories === "number") {
        totalCalories += recipe.calories * selectedRecipes[recipeId];
      }
    });
    return totalCalories;
  }, [selectedRecipes, allRecipes]);

  const handleAddSelectedRecipes = () => {
    const recipesToAdd = Object.keys(selectedRecipes)
      .filter((recipeId) => selectedRecipes[recipeId] > 0)
      .map((recipeId) => {
        const recipe = allRecipes.find((r) => r._id === recipeId);
        return {
          recipe: recipe,
          quantity: selectedRecipes[recipeId],
        };
      })
      .filter((item) => item.recipe);

    if (recipesToAdd.length === 0) {
      antdMessage.warning("Vui lòng chọn ít nhất một món ăn để thêm.");
      return;
    }

    onAddRecipe(mealType, recipesToAdd);
  };

  if (!isOpen) return null;

  const mealTypeDisplay =
    {
      breakfast: "Bữa sáng",
      lunch: "Bữa trưa",
      dinner: "Bữa tối",
      snack: "Bữa ăn vặt",
    }[mealType] || mealType;

  const selectedCount = Object.keys(selectedRecipes).length;
  const totalSelectedItems = Object.values(selectedRecipes).reduce(
    (sum, qty) => sum + qty,
    0
  );
  const currentTotalCalories = calculateSelectedRecipesCalories();
  const handleDropdownSelect = ({ key }) => {
    if (filterMode === key) return; // ⛔ Không làm gì nếu đã là chế độ hiện tại

    setFilterMode(key);

    if (key === "all") {
      loadAllRecipesInitial();
    } else if (key === "suggested") {
      fetchSuggestedRecipes();
    }
  };

  const dropdownMenu = (
    <Menu
      onClick={handleDropdownSelect}
      items={[
        {
          key: "all",
          label: "📋 Tất cả món ăn",
          disabled: filterMode === "all",
        },
        {
          key: "suggested",
          label: "✨ Gợi ý món ăn",
          disabled: filterMode === "suggested",
        },
      ]}
    />
  );

  return (
    <ModalBackground onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          <ModalTitle>Thêm món ăn</ModalTitle>
          <ModalSubtitle>Chọn món ăn cho {mealTypeDisplay}</ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          <SearchSection>
            <SearchInputWrapper>
              <SearchIcon>🔍</SearchIcon>
              <SearchInput
                type="text"
                placeholder="Tìm kiếm thông minh"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInputWrapper>
            <Dropdown overlay={dropdownMenu} trigger={["click"]}>
              <LoadAllButton>
                {filterMode === "suggested"
                  ? "✨ Gợi ý món ăn"
                  : "📋 Tất cả món ăn"}{" "}
                <DownOutlined />
              </LoadAllButton>
            </Dropdown>
          </SearchSection>

          <StatusCard type={error ? "error" : loading ? "loading" : "info"}>
            <strong>Trạng thái:</strong> {debugInfo}
            <br />
            <strong>Số món hiển thị:</strong> {filteredRecipes.length}
            <br />
            <strong>Đã chọn:</strong> {selectedCount} món ({totalSelectedItems}{" "}
            phần)
          </StatusCard>

          {loading && (
            <StatusCard type="loading">
              ⏳ Đang tải dữ liệu, vui lòng đợi...
            </StatusCard>
          )}

          {error && <StatusCard type="error">❌ {error}</StatusCard>}

          <RecipeGrid>
            {filteredRecipes.length > 0
              ? filteredRecipes.map((recipe) => (
                  <RecipeCard
                    key={recipe._id}
                    selected={selectedRecipes[recipe._id] > 0}
                  >
                    <RecipeCardContent>
                      <RecipeImage
                        src={
                          recipe.imageUrl || "https://via.placeholder.com/60"
                        } // Fallback image nếu không có
                        alt={recipe.name}
                      />
                      <RecipeDetails>
                        <RecipeName>{recipe.name}</RecipeName>
                        <RecipeCalories>
                          🔥 {recipe.calories || 0} kcal
                        </RecipeCalories>
                      </RecipeDetails>
                      <QuantityControl>
                        <QuantityButton
                          onClick={() => handleQuantityChange(recipe._id, -1)}
                          disabled={!selectedRecipes[recipe._id]}
                        >
                          −
                        </QuantityButton>
                        <QuantityDisplay>
                          {selectedRecipes[recipe._id] || 0}
                        </QuantityDisplay>
                        <QuantityButton
                          onClick={() => handleQuantityChange(recipe._id, 1)}
                        >
                          +
                        </QuantityButton>
                      </QuantityControl>
                    </RecipeCardContent>
                  </RecipeCard>
                ))
              : !loading && (
                  <EmptyState>
                    <EmptyStateIcon>{searchTerm ? "🔍" : "🍽️"}</EmptyStateIcon>
                    <EmptyStateText>
                      {searchTerm
                        ? `Không tìm thấy món ăn nào phù hợp với "${searchTerm}".`
                        : 'Nhập từ khóa để tìm kiếm hoặc nhấn "Tất cả món ăn" để xem danh sách đầy đủ.'}
                    </EmptyStateText>
                    {searchTerm && (
                      <SearchTips>
                        💡 <strong>Mẹo tìm kiếm:</strong>
                        <br />
                        • Gõ ký tự đầu: "c" → tìm "cơm gà"
                        <br />
                        • Gõ không dấu: "com ga" → tìm "cơm gà" <br />• Gõ một
                        phần: "gà" → tìm tất cả món có "gà"
                      </SearchTips>
                    )}
                  </EmptyState>
                )}
          </RecipeGrid>
        </ModalBody>

        {selectedCount > 0 && (
          <FloatingActionButton onClick={handleAddSelectedRecipes}>
            ✨ Thêm {selectedCount} món đã chọn ({totalSelectedItems} phần) -{" "}
            {Math.round(currentTotalCalories)} kcal
          </FloatingActionButton>
        )}
      </ModalContent>
    </ModalBackground>
  );
};

export default AddRecipeModal;
